/**
 * Central de Projetos: lista única de pendências, em checklist por cliente.
 *
 * Filtros por cliente, projetista, tipo e idade. Ações em lote só fecham ou
 * reatribuem, sempre com confirmação (regra do briefing).
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { agirEmLote, listarPendencias, listarResponsaveis } from "../../services/centralProjetos";
import { plural } from "../../utils/dinheiro";
import { TIPOS_PENDENCIA, agruparPorCliente, quandoFoi, textoPrazo } from "./apresentacao";
import { ErroDaCentral, MensagensDeOrigem, PERM, useCarga } from "./comum";
import { BotaoVirarTarefa, usePendenciasNaAgenda } from "./NaAgenda";
import {
  Acoes,
  Botao,
  Campo,
  Filtros,
  Item,
  Linha,
  Lista,
  Pagina,
  Ponto,
  Principal,
  Secao,
  SecaoTitulo,
  Secundario,
  Titulo,
  Topo,
  Vazio,
} from "./styles";

const IDADES = [
  { valor: "", rotulo: "Qualquer idade" },
  { valor: "1", rotulo: "Mais de 1 dia" },
  { valor: "3", rotulo: "Mais de 3 dias" },
  { valor: "7", rotulo: "Mais de 7 dias" },
];

export default function PendenciasCentral() {
  const navigate = useNavigate();
  const toast = useToast();
  const { request, hasPermission } = useAuth();
  const podeTratar = Boolean(hasPermission?.(PERM.TRATAR));
  const [filtros, setFiltros] = useState({ tipo: "", idade_dias: "", projetista: "", cliente: "" });
  const [marcadas, setMarcadas] = useState(new Set());
  const [confirmar, setConfirmar] = useState(null);
  const [responsaveis, setResponsaveis] = useState([]);
  const [responsavel, setResponsavel] = useState("");
  const { dados, erro, carregando, recarregar } = useCarga(
    (req) => listarPendencias(req, filtros),
    [filtros.tipo, filtros.idade_dias, filtros.projetista, filtros.cliente],
  );

  useEffect(() => {
    if (!podeTratar) return;
    listarResponsaveis(request).then((lista) => setResponsaveis(lista || [])).catch(() => setResponsaveis([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeTratar]);

  const grupos = useMemo(() => agruparPorCliente(dados?.pendencias), [dados]);
  const naAgenda = usePendenciasNaAgenda(useMemo(() => (dados?.pendencias || []).map((p) => String(p.id)), [dados]));
  const alterar = (campo) => (e) => {
    setMarcadas(new Set());
    setFiltros((atual) => ({ ...atual, [campo]: e.target.value }));
  };
  const alternar = (id) =>
    setMarcadas((atual) => {
      const nova = new Set(atual);
      if (nova.has(id)) nova.delete(id);
      else nova.add(id);
      return nova;
    });

  async function executarLote() {
    const corpo = confirmar === "atribuir"
      ? { acao: "atribuir", ids: [...marcadas], usuario_id: responsavel || null }
      : { acao: "fechar", estado: confirmar, ids: [...marcadas] };
    const resposta = await agirEmLote(request, corpo);
    toast.success(`${plural(resposta.feitos, "pendência atualizada", "pendências atualizadas")}.`);
    if (resposta.erros?.length) toast.warning(`${resposta.erros.length} não puderam ser alteradas.`);
    setConfirmar(null);
    setMarcadas(new Set());
    recarregar();
  }

  const textoConfirmacao = {
    resolvida: "Marcar como resolvidas",
    descartada: "Descartar",
    atribuir: "Reatribuir",
  }[confirmar];

  return (
    <Pagina>
      <Topo>
        <Botao type="button" aria-label="Voltar ao painel" title="Voltar" onClick={() => navigate("/central-projetos")}>
          <FiArrowLeft aria-hidden="true" />
        </Botao>
        <Titulo>
          <h1>Pendências</h1>
          <p>{dados ? `${plural(dados.total, "pendência aberta", "pendências abertas")}` : "Carregando…"}</p>
        </Titulo>
      </Topo>

      <Filtros>
        <Campo>
          Tipo
          <select value={filtros.tipo} onChange={alterar("tipo")}>
            <option value="">Todos</option>
            {TIPOS_PENDENCIA.map((t) => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
          </select>
        </Campo>
        <Campo>
          Idade
          <select value={filtros.idade_dias} onChange={alterar("idade_dias")}>
            {IDADES.map((i) => <option key={i.valor} value={i.valor}>{i.rotulo}</option>)}
          </select>
        </Campo>
        <Campo>
          Projetista
          <input value={filtros.projetista} onChange={alterar("projetista")} placeholder="Nome como na tela Executivo" />
        </Campo>
        <Campo>
          Pedido
          <input value={filtros.cliente} onChange={alterar("cliente")} placeholder="Ex.: 1418" inputMode="numeric" />
        </Campo>
      </Filtros>

      {podeTratar && marcadas.size > 0 ? (
        <Acoes role="toolbar" aria-label="Ações em lote">
          <Secundario>{plural(marcadas.size, "marcada", "marcadas")}</Secundario>
          <Botao type="button" onClick={() => setConfirmar("resolvida")}>Resolver</Botao>
          <Botao type="button" onClick={() => setConfirmar("descartada")}>Descartar</Botao>
          <Campo as="div" style={{ minWidth: 160 }}>
            <select aria-label="Reatribuir a" value={responsavel} onChange={(e) => setResponsavel(e.target.value)}>
              <option value="">Sem responsável</option>
              {responsaveis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </Campo>
          <Botao type="button" onClick={() => setConfirmar("atribuir")}>Reatribuir</Botao>
        </Acoes>
      ) : null}

      {erro ? <ErroDaCentral erro={erro} /> : null}
      {!carregando && dados && grupos.length === 0 ? <Lista><Vazio>Nenhuma pendência com esses filtros.</Vazio></Lista> : null}
      {grupos.map((grupo) => (
        <Secao key={grupo.numero_pedido} aria-label={`Pendências de ${grupo.cliente}`}>
          <SecaoTitulo>
            <h2>
              <button
                type="button"
                onClick={() => navigate(`/central-projetos/clientes/${encodeURIComponent(grupo.numero_pedido)}`)}
                style={{ all: "unset", cursor: "pointer" }}
              >
                {grupo.cliente || "Cliente"} · {grupo.numero_pedido}
              </button>
            </h2>
            <span>{grupo.itens.length}</span>
          </SecaoTitulo>
          <Lista>
            {grupo.itens.map((p) => (
              <Item key={p.id}>
                <Linha $colunas={podeTratar ? "28px minmax(0, 1fr)" : "minmax(0, 1fr)"}>
                  {podeTratar ? (
                    <input
                      type="checkbox"
                      aria-label={`Marcar: ${p.descricao}`}
                      checked={marcadas.has(p.id)}
                      onChange={() => alternar(p.id)}
                      style={{ width: 18, height: 18 }}
                    />
                  ) : null}
                  <div style={{ minWidth: 0 }}>
                    <Principal style={{ whiteSpace: "normal" }}>
                      <Ponto $tom={p.tipo === "sem_resposta" || p.tipo === "aguardando_equipe" ? "warning" : undefined} aria-hidden="true" />
                      {p.descricao}
                    </Principal>
                    <Secundario>
                      {p.tipo_rotulo} · {textoPrazo(p.prazo)} · aberta {quandoFoi(p.criada_em)}
                      {p.atribuida_a ? ` · com ${p.atribuida_a.nome}` : ""}
                      {p.resolucao_sugerida_em ? " · parece resolvida" : ""}
                    </Secundario>
                    <div style={{ marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <MensagensDeOrigem ids={p.mensagens_origem} />
                      {naAgenda.liberada ? <BotaoVirarTarefa pendencia={p} tarefaId={naAgenda.mapa[String(p.id)]} onVirou={naAgenda.marcar} /> : null}
                    </div>
                  </div>
                </Linha>
              </Item>
            ))}
          </Lista>
        </Secao>
      ))}

      <ConfirmDialog
        open={Boolean(confirmar)}
        titulo={`${textoConfirmacao || ""} ${plural(marcadas.size, "pendência", "pendências")}?`}
        descricao="A ação fica registrada na auditoria com o seu nome."
        confirmarLabel={textoConfirmacao}
        tone={confirmar === "descartada" ? "danger" : "primary"}
        onConfirmar={executarLote}
        onCancelar={() => setConfirmar(null)}
      />
    </Pagina>
  );
}
